import { localeRowDirection } from '@/lib/rtl-layout';
import { useEffect, useRef, useState } from "react";
import {
  Platform,
  Pressable,
  StyleSheet,
  View,
  type StyleProp,
  type TextInputProps,
  type ViewStyle,
} from "react-native";
import { Check, Eye, EyeOff } from "lucide-react-native";
import { useTranslation } from "react-i18next";
import { colors } from "@tabbio/tokens";
import {
  evaluatePassword,
  PASSWORD_MAX_LENGTH,
  type PasswordRuleKey,
} from "@tabbio/utils";
import { Text, TextInput } from "@/components/common/AppText";
import { useTheme } from "@/hooks/useTheme";
import { useLocale } from "@/hooks/useLocale";
import { useFont } from "@/providers/FontProvider";
import { useHaptics } from "@/hooks/useHaptics";

/**
 * Reusable password input for apps/mobile (issues #26, #27, #25 groundwork).
 *
 * - Show/Hide toggle (eye / eye-off) to reveal the typed password (#27).
 * - Structurally stable layout: fixed input height + padding and a fixed-size
 *   trailing slot for the toggle, so nothing reflows whether the text is
 *   secured/visible or empty/filled (#26).
 * - Opt-in live rule checklist driven by `evaluatePassword` from
 *   `@tabbio/utils` via the `showRules` prop (#25 groundwork).
 *
 * RTL: the toggle and checklist account for native mirroring, including the
 * interval before a language-switch reload. Password characters stay LTR.
 */

const TRAILING_SLOT_WIDTH = 44;
const ICON_SIZE = 20;
// Clearance for iOS's native caps-lock badge on secure fields, which draws
// over the Arabic-placeholder overlay near the real input's own edge
// regardless of the input's own padding/margin. Value tuned against a live
// simulator (issues #122, #123).
const CAPS_LOCK_BADGE_CLEARANCE = 64;

const RULE_ORDER: readonly PasswordRuleKey[] = [
  "minLength",
  "hasLetter",
  "hasNumber",
  "hasSpecial",
];

export interface PasswordFieldProps {
  value: string;
  onChangeText: (value: string) => void;
  placeholder?: string;
  placeholderTextColor?: string;
  /** Renders an error border. Pass the message via `errorText` if you want it inline. */
  error?: boolean;
  autoFocus?: boolean;
  returnKeyType?: TextInputProps["returnKeyType"];
  onSubmitEditing?: TextInputProps["onSubmitEditing"];
  textContentType?: TextInputProps["textContentType"];
  autoComplete?: TextInputProps["autoComplete"];
  editable?: boolean;
  /** When true, renders a live per-rule checklist below the input. */
  showRules?: boolean;
  /** Optional style override for the outer container. */
  containerStyle?: StyleProp<ViewStyle>;
  /** Called when focus enters the input (e.g. web focus rings). */
  onFocus?: TextInputProps["onFocus"];
  /** Called when focus leaves the input. */
  onBlur?: TextInputProps["onBlur"];
}

export function PasswordField({
  value,
  onChangeText,
  placeholder,
  placeholderTextColor,
  error = false,
  autoFocus,
  returnKeyType,
  onSubmitEditing,
  textContentType,
  autoComplete,
  editable = true,
  showRules = false,
  containerStyle,
  onFocus,
  onBlur,
}: PasswordFieldProps) {
  const { theme } = useTheme();
  const { t } = useTranslation("auth");
  const { isRTL } = useLocale();
  const { fonts } = useFont();
  const haptics = useHaptics();
  // RN Web resolves start/end padding from its dir context, not document CSS.
  const webDirectionProps = Platform.OS === "web"
    ? { dir: isRTL ? "rtl" : "ltr" }
    : {};

  const [visible, setVisible] = useState(false);
  const secureInputRef = useRef<TextInput>(null);
  const plainInputRef = useRef<TextInput>(null);
  const refocusAfterVisibilityChangeRef = useRef(false);

  useEffect(() => {
    if (!refocusAfterVisibilityChangeRef.current) return;

    const frame = requestAnimationFrame(() => {
      (visible ? plainInputRef : secureInputRef).current?.focus();
      refocusAfterVisibilityChangeRef.current = false;
    });

    return () => cancelAnimationFrame(frame);
  }, [visible]);

  const toggleVisible = () => {
    haptics.trigger("light");
    // UIKit can retain the secure-field Caps Lock badge after a UITextField's
    // `secureTextEntry` mode changes. iOS therefore uses two persistent native
    // fields below: one is always secure and the other is always plain. Move
    // focus between them without ever mutating either field's security trait.
    // Web and Android keep their single input node and normal cursor behavior.
    refocusAfterVisibilityChangeRef.current =
      Platform.OS === "ios" &&
      (visible ? plainInputRef : secureInputRef).current?.isFocused() === true;
    setVisible((v) => !v);
  };

  const evaluation = showRules ? evaluatePassword(value) : null;

  const ToggleIcon = visible ? EyeOff : Eye;

  return (
    <View {...webDirectionProps} style={containerStyle}>
      <View
        style={[
          styles.inputRow,
          {
            flexDirection: localeRowDirection(isRTL),
            borderColor: error ? colors.danger : theme.border,
            backgroundColor: theme.background,
            opacity: editable ? 1 : 0.5,
          },
        ]}
      >
        {Platform.OS === "ios" ? (
          <View style={styles.iosInputSlot}>
            <View
              pointerEvents={visible ? "none" : "auto"}
              accessibilityElementsHidden={visible}
              importantForAccessibility={
                visible ? "no-hide-descendants" : "auto"
              }
              style={[
                styles.iosInputLayer,
                visible && styles.inactiveInputLayer,
              ]}
            >
              <TextInput
                ref={secureInputRef}
                style={[
                  styles.input,
                  {
                    color: theme.text,
                    textAlign: isRTL ? "right" : "left",
                    writingDirection: "ltr",
                  },
                ]}
                value={value}
                onChangeText={onChangeText}
                placeholder={placeholder}
                placeholderTextColor={
                  placeholderTextColor ?? theme.textTertiary
                }
                secureTextEntry
                maxLength={PASSWORD_MAX_LENGTH}
                placeholderInsetEnd={CAPS_LOCK_BADGE_CLEARANCE}
                autoCapitalize="none"
                autoCorrect={false}
                spellCheck={false}
                editable={editable && !visible}
                autoFocus={autoFocus}
                returnKeyType={returnKeyType}
                onSubmitEditing={onSubmitEditing}
                textContentType={textContentType}
                autoComplete={autoComplete}
                onFocus={onFocus}
                onBlur={onBlur}
              />
            </View>

            <View
              pointerEvents={visible ? "auto" : "none"}
              accessibilityElementsHidden={!visible}
              importantForAccessibility={
                visible ? "auto" : "no-hide-descendants"
              }
              style={[
                styles.iosInputLayer,
                !visible && styles.inactiveInputLayer,
              ]}
            >
              <TextInput
                ref={plainInputRef}
                style={[
                  styles.input,
                  {
                    color: theme.text,
                    textAlign: isRTL ? "right" : "left",
                    writingDirection: "ltr",
                  },
                ]}
                // Do not retain a second plain-text copy while the password is
                // hidden; populate this field only for the user's reveal mode.
                value={visible ? value : ""}
                onChangeText={onChangeText}
                placeholder={placeholder}
                placeholderTextColor={
                  placeholderTextColor ?? theme.textTertiary
                }
                secureTextEntry={false}
                maxLength={PASSWORD_MAX_LENGTH}
                autoCapitalize="none"
                autoCorrect={false}
                spellCheck={false}
                editable={editable && visible}
                returnKeyType={returnKeyType}
                onSubmitEditing={onSubmitEditing}
                textContentType="none"
                autoComplete="off"
                onFocus={onFocus}
                onBlur={onBlur}
              />
            </View>
          </View>
        ) : (
          <TextInput
            style={[
              styles.input,
              {
                color: theme.text,
                textAlign: isRTL ? "right" : "left",
                // Latin password chars/dots always read LTR even in Arabic UI.
                writingDirection: "ltr",
              },
            ]}
            value={value}
            onChangeText={onChangeText}
            placeholder={placeholder}
            placeholderTextColor={placeholderTextColor ?? theme.textTertiary}
            secureTextEntry={!visible}
            maxLength={PASSWORD_MAX_LENGTH}
            autoCapitalize="none"
            autoCorrect={false}
            spellCheck={false}
            editable={editable}
            autoFocus={autoFocus}
            returnKeyType={returnKeyType}
            onSubmitEditing={onSubmitEditing}
            textContentType={textContentType}
            autoComplete={autoComplete}
            onFocus={onFocus}
            onBlur={onBlur}
          />
        )}

        <Pressable
          onPress={toggleVisible}
          disabled={!editable}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel={
            visible
              ? t("passwordField.hide", { defaultValue: "Hide password" })
              : t("passwordField.show", { defaultValue: "Show password" })
          }
          style={styles.toggle}
        >
          <ToggleIcon
            size={ICON_SIZE}
            color={theme.textTertiary}
            strokeWidth={1.8}
          />
        </Pressable>
      </View>

      {evaluation ? (
        <View style={styles.rules}>
          {RULE_ORDER.map((key) => {
            const met = evaluation[key];
            return (
              <View
                key={key}
                style={[
                  styles.ruleRow,
                  { flexDirection: localeRowDirection(isRTL) },
                ]}
              >
                <View
                  style={[
                    styles.ruleIcon,
                    {
                      backgroundColor: met ? colors.success : "transparent",
                      borderColor: met ? colors.success : theme.border,
                    },
                  ]}
                >
                  {met ? (
                    <Check size={11} color={theme.background} strokeWidth={3} />
                  ) : null}
                </View>
                <Text
                  style={[
                    styles.ruleLabel,
                    {
                      color: met ? theme.textSecondary : theme.textTertiary,
                      fontFamily: fonts.regular,
                      textAlign: isRTL ? "right" : "left",
                      direction: "ltr",
                      writingDirection: isRTL ? "rtl" : "ltr",
                    },
                  ]}
                >
                  {t(`passwordField.rules.${key}`)}
                </Text>
              </View>
            );
          })}
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  inputRow: {
    alignItems: "center",
    borderRadius: 9999,
    borderWidth: 1,
    // Fixed height + padding keep the field from reflowing while typing.
    height: 56,
    paddingStart: 20,
    paddingEnd: 6,
    width: "100%",
  },
  input: {
    flex: 1,
    fontSize: 16,
    letterSpacing: 0,
    // Match the parent height so the caret/text baseline never shifts.
    height: "100%",
    paddingVertical: Platform.OS === "web" ? 0 : 4,
  },
  iosInputSlot: {
    flex: 1,
    height: "100%",
    position: "relative",
  },
  iosInputLayer: {
    ...StyleSheet.absoluteFillObject,
  },
  inactiveInputLayer: {
    opacity: 0,
  },
  toggle: {
    width: TRAILING_SLOT_WIDTH,
    height: 44,
    alignItems: "center",
    justifyContent: "center",
  },
  rules: {
    marginTop: 10,
    gap: 6,
    paddingStart: 4,
  },
  ruleRow: {
    alignItems: "center",
    gap: 8,
  },
  ruleIcon: {
    width: 16,
    height: 16,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  ruleLabel: {
    fontSize: 13,
    lineHeight: 18,
  },
});
